import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../../lib/analytics';
import { useConsultaModal } from '../../context/ConsultaModalContext';

export const VanguardNav = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const { open } = useConsultaModal();

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) setMobileOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        window.dispatchEvent(new CustomEvent('nav:menu', { detail: { open: mobileOpen } }));
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const navLinks = [
        { label: 'Início', to: '/' },
        { label: 'Sobre', to: '/#about' },
        { label: 'Serviços', to: '/#services' },
    ];

    return (
        <>
            <nav className="sticky top-0 z-50 bg-white/80 dark:bg-vanguard-background-dark/80 backdrop-blur-md border-b border-stone-200 dark:border-stone-800 font-vanguard-sans transition-colors duration-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-24 items-center">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-3">
                            <img src="/logo/Logo-Pacces-Advogados-Color.png" alt="Pacces Advogados" className="h-16 w-auto block dark:hidden" />
                            <img src="/logo/Logo-Pacces-Advogados-Branco.png" alt="Pacces Advogados" className="h-16 w-auto hidden dark:block" />
                        </Link>

                        {/* Desktop nav */}
                        <div className="hidden md:flex space-x-8 items-center text-xs font-bold uppercase tracking-widest text-vanguard-deep-brown dark:text-stone-300">
                            {navLinks.map((link) => (
                                <Link key={link.label} className="hover:text-vanguard-primary transition-colors" to={link.to}>
                                    {link.label}
                                </Link>
                            ))}
                            <button
                                onClick={() => { open(); trackEvent('nav_cta_click_vanguard', { cta: 'agende_consulta' }); }}
                                className="bg-transparent border border-pacces-gold text-pacces-gold px-6 py-2.5 text-[12px] font-bold uppercase tracking-[0.2em] rounded-sm hover:bg-pacces-gold hover:text-pacces-ebony transition-all duration-300 shadow-sm active:scale-95"
                            >
                                Agende Consulta
                            </button>
                        </div>

                        {/* Hamburger (mobile) */}
                        <button
                            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
                            aria-expanded={mobileOpen}
                            className="md:hidden flex flex-col justify-center items-center gap-[5px] w-11 h-11 cursor-pointer"
                            onClick={() => setMobileOpen((v) => !v)}
                        >
                            <span className={`block h-[2px] bg-vanguard-deep-brown dark:bg-white transition-all duration-300 ${mobileOpen ? 'w-5 translate-y-[7px] rotate-45' : 'w-5'}`} />
                            <span className={`block h-[2px] bg-vanguard-deep-brown dark:bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0 w-5' : 'w-4'}`} />
                            <span className={`block h-[2px] bg-vanguard-deep-brown dark:bg-white transition-all duration-300 ${mobileOpen ? 'w-5 -translate-y-[7px] -rotate-45' : 'w-5'}`} />
                        </button>
                    </div>
                </div>
            </nav>

            {/* Overlay */}
            <div
                className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 md:hidden ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setMobileOpen(false)}
                aria-hidden="true"
            />

            {/* Mobile drawer */}
            <div
                className={`fixed top-0 right-0 z-50 h-full w-72 max-w-[85vw] bg-white dark:bg-vanguard-background-dark border-l border-stone-200 dark:border-stone-800 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out md:hidden ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-6 border-b border-stone-200 dark:border-stone-800">
                    <img src="/logo/Logo-Pacces-Advogados-Color.png" alt="Pacces Advogados" className="h-10 w-auto dark:hidden" />
                    <img src="/logo/Logo-Pacces-Advogados-Branco.png" alt="Pacces Advogados" className="h-10 w-auto hidden dark:block" />
                    <button
                        aria-label="Fechar menu"
                        className="w-11 h-11 flex items-center justify-center text-stone-500 hover:text-vanguard-primary transition-colors cursor-pointer"
                        onClick={() => setMobileOpen(false)}
                    >
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Links */}
                <nav className="flex-1 flex flex-col gap-1 px-4 py-8">
                    {navLinks.map((link) => (
                        <Link
                            key={link.label}
                            to={link.to}
                            onClick={() => setMobileOpen(false)}
                            className="px-4 py-4 text-[12px] font-bold uppercase tracking-[0.25em] text-vanguard-deep-brown dark:text-stone-300 hover:text-vanguard-primary hover:bg-stone-50 dark:hover:bg-stone-900 rounded-sm transition-all duration-200"
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                {/* CTA */}
                <div className="px-6 pb-10">
                    <button
                        onClick={() => {
                            setMobileOpen(false);
                            open();
                            trackEvent('nav_cta_click_vanguard', { cta: 'agende_consulta_mobile' });
                        }}
                        className="block w-full text-center bg-pacces-gold text-pacces-ebony px-8 py-4 text-[12px] font-bold uppercase tracking-[0.25em] rounded-sm hover:bg-pacces-ebony hover:text-pacces-gold transition-all duration-300 active:scale-95"
                    >
                        Agende Consulta
                    </button>
                </div>
            </div>
        </>
    );
};
